import {
    ActionIcon,
    Badge,
    Checkbox,
    Chip,
    Group,
    MantineStyleProp,
    Popover,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
    Tooltip,
} from "@mantine/core";
import { useDebouncedState, useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { RefObject, useId, useState } from "react";
import { GroupChildrenRepository, GroupParentsRepository, GroupSearchRepository } from "shared/components/organization/groups/data";
import classes from "shared/components/organization/groups/groups.module.css";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { permissionCheck } from "shared/stores/oidc";
import { FullGroupLikeFragment, GroupLikeFragment, Permission, PermissionLikeFragment } from "src/gql/graphql";
import { match } from "ts-pattern";
import { container } from "tsyringe";

export function sharedOrgPageGroupDisabledCheck(permissions: PermissionLikeFragment, group: GroupLikeFragment) {
    return !permissionCheck(permissions, Permission.GroupSubgroupManage, group.id);
}

function mergeAndDeduplicateGroups(
    initialGroups: GroupLikeFragment[] | undefined,
    queriedGroups: FullGroupLikeFragment[] | undefined,
): FullGroupLikeFragment[] {
    const queried = queriedGroups ?? [];
    if (!initialGroups) return queried;

    const queriedIds = new Set(queried.map((g) => g.id));
    const uniqueInitialGroups = initialGroups
        .filter((g) => !queriedIds.has(g.id))
        .map((g) => ({ ...g, parentsPaged: [], childrenPaged: [] }));

    return [...queried, ...uniqueInitialGroups];
}

const GroupRow = ({
    disabled,
    group,
    onChange,
    checked,
}: {
    disabled?: boolean;
    checked: boolean;
    group: FullGroupLikeFragment;
    onChange: (group: FullGroupLikeFragment, checked: boolean) => unknown;
}) => {
    const titleId = useId();

    return (
        <Stack flex={1}>
            <Group maw="100%" wrap="nowrap" justify="space-between" flex={1} gap={0}>
                <Stack gap={0} maw="90%">
                    <Title id={titleId} order={3} size="h4" fw="normal" style={{ textDecoration: checked ? "underline" : undefined }}>
                        {group.name}
                    </Title>
                    {group.parentsPaged.length > 0 && (
                        <Group wrap="nowrap" gap="xs">
                            <Text fz="sm" fs="italic" c="dimmed">
                                Parent(s):
                            </Text>
                            <Text fz="sm" fs="italic" style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                {group.parentsPaged
                                    .slice(0, 3)
                                    .map((it) => it.name)
                                    .join(", ")}
                                {group.parentsPaged.length > 3 && "..."}
                            </Text>
                        </Group>
                    )}
                </Stack>
                <Tooltip disabled={!disabled} label="You cannot select this group">
                    <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onChange={(e) => onChange(group, e.currentTarget.checked)}
                        aria-labelledby={titleId}
                    />
                </Tooltip>
            </Group>
        </Stack>
    );
};

export const GroupSelector = ({
    onChange,
    shouldDisable,
    clickOutsideRef,
    onClose,
    selectedGroups,
    filter,
    containerStyle,
    withClose,
    initialGroups,
}: {
    onChange: (group: GroupLikeFragment, checked: boolean) => unknown;
    shouldDisable: (group: GroupLikeFragment) => boolean;
    onClose?: () => void;
    clickOutsideRef?: RefObject<HTMLDivElement>;
    selectedGroups: GroupLikeFragment[];
    filter?: (group: FullGroupLikeFragment) => boolean;
    containerStyle?: MantineStyleProp;
    withClose?: boolean;
    initialGroups?: GroupLikeFragment[];
}) => {
    const isMobile = useIsMobile();

    const [filterMode, { toggle: toggleFilterMode }] = useDisclosure();
    const [groupSearch, setGroupSearch] = useDebouncedState("", 300);
    const [groupFilter, setGroupFilter] = useState("");
    const [debouncedGroupFilter] = useDebouncedValue(groupFilter, 300);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState<FullGroupLikeFragment>();

    // query to populate the filter by parent dropdown
    const groupSearchRepo = container.resolve(GroupSearchRepository);
    const groupFilterSearchLoader = groupSearchRepo.get({ query: debouncedGroupFilter, queryMembership: false });
    const groupFilterSearchResults = groupFilterSearchLoader?.useItems();

    // query after filtering by a parent
    const parentId = selectedGroupFilter?.id ?? "";
    const groupChildrenRepo = container.resolve(GroupChildrenRepository);
    const groupChildrenLoader = groupChildrenRepo.get({ parentId, queryMembership: false });
    const groupChildrenData = groupChildrenLoader.useItems();

    // default query, optionally filtered by a name search
    const groupSearchLoader = groupSearchRepo.get({ query: groupSearch, queryMembership: false });
    const groupsData = groupSearchLoader.useItems();

    const queriedGroups = filterMode ? groupChildrenData : groupsData;
    const groups = mergeAndDeduplicateGroups(groupSearch || filterMode ? undefined : initialGroups, queriedGroups);

    const filteredGroups = !!filter ? groups.filter(filter) : groups;

    return (
        <Stack ref={clickOutsideRef} style={{ overflow: "hidden", height: "65dvh", ...containerStyle }}>
            <Group>
                <SegmentedControl
                    data={["Search", "Filter"]}
                    onChange={() => {
                        setGroupSearch("");
                        setGroupFilter("");
                        setSelectedGroupFilter(undefined);
                        toggleFilterMode();
                    }}
                    flex={1}
                />
                {withClose && (
                    <ActionIcon variant="transparent" onClick={onClose} aria-label="Close group selector">
                        <IconX size={16} />
                    </ActionIcon>
                )}
            </Group>
            {filterMode ? (
                <Select
                    comboboxProps={{ withinPortal: false }}
                    label="Filter by Parent"
                    placeholder="Filter groups..."
                    searchable
                    clearable
                    searchValue={groupFilter}
                    onSearchChange={setGroupFilter}
                    onChange={(id) => setSelectedGroupFilter(groupFilterSearchResults?.find((it) => it.id == id))}
                    data={groupFilterSearchResults?.map((it) => ({ label: it.name, value: it.id }))}
                />
            ) : (
                <TextInput label="Search Groups" placeholder="Search groups..." onChange={(e) => setGroupSearch(e.currentTarget.value)} />
            )}
            <Group gap="xs" wrap={isMobile ? "wrap" : "nowrap"} maw="calc(100%)" pb="md" style={{ overflowX: "auto" }}>
                <Text fz="sm" fs="italic" c="dimmed">
                    Selected:
                </Text>
                {selectedGroups.map((it) => (
                    <Chip
                        disabled={shouldDisable(it)}
                        size="xs"
                        defaultChecked
                        icon={<IconX size={12} />}
                        key={"chip" + it.id}
                        onClick={() => {
                            onChange(it, false);
                        }}
                    >
                        {it.name}
                    </Chip>
                ))}
            </Group>
            <Stack flex={1} justify="cetner" pr="md" style={{ overflowY: "auto" }}>
                {!!selectedGroupFilter && (
                    <Group pos="sticky" top={0} style={{ backgroundColor: "var(--mantine-color-body)" }}>
                        <GroupRow
                            group={selectedGroupFilter}
                            onChange={onChange}
                            checked={selectedGroups.some((it) => it.id == selectedGroupFilter.id)}
                            disabled={shouldDisable(selectedGroupFilter)}
                        />
                    </Group>
                )}
                <Stack gap="xl" pl={filterMode ? "md" : undefined}>
                    {(filteredGroups?.length ?? 0) > 0 ? (
                        filteredGroups?.map((g) => (
                            <GroupRow
                                key={g.id}
                                checked={selectedGroups.some((it) => it.id == g.id)}
                                group={g}
                                onChange={onChange}
                                disabled={shouldDisable(g)}
                            />
                        ))
                    ) : !!selectedGroupFilter ? (
                        <Title order={3} fs="italic" c="dimmed" mx="auto" w="fit-content">
                            Group has no child groups
                        </Title>
                    ) : !filterMode ? (
                        <Title order={3} fs="italic" c="dimmed" mx="auto" w="fit-content">
                            No results
                        </Title>
                    ) : null}
                </Stack>
                {filterMode && !selectedGroupFilter?.id ? (
                    <Title order={3} fs="italic" c="dimmed" mx="auto">
                        Select a parent group
                    </Title>
                ) : filterMode && !!groupChildrenLoader ? (
                    <groupChildrenLoader.LoadTrigger />
                ) : (
                    <groupSearchLoader.LoadTrigger />
                )}
            </Stack>
        </Stack>
    );
};

const GroupBadge = (props: { id: string; name: string; onClick: () => void }) => (
    <Link
        to="/organization/groups/$groupId"
        params={{ groupId: props.id }}
        onClick={props.onClick}
        className={classes.viewMoreDropdownItem}
        style={{ cursor: "pointer", width: 200 }}
    >
        <Text truncate>{props.name}</Text>
    </Link>
);

const ViewMoreParents = (props: { childId: string; onClick: () => void }) => {
    const repo = container.resolve(GroupParentsRepository);
    const loader = repo.get({ childId: props.childId, queryMembership: false });
    const data = loader.useItems();

    return (
        <Stack mah={200} gap={0} style={{ overflowY: "auto" }}>
            {data.map((p) => (
                <GroupBadge key={p.id} id={p.id} name={p.name} onClick={props.onClick} />
            ))}
            <loader.LoadTrigger />
        </Stack>
    );
};

const ViewMoreChildren = (props: { parentId: string; onClick: () => void }) => {
    const repo = container.resolve(GroupChildrenRepository);
    const loader = repo.get({ parentId: props.parentId, queryMembership: false });
    const data = loader.useItems();

    return (
        <Stack mah={200} gap={0} style={{ overflowY: "auto" }}>
            {data.map((p) => (
                <GroupBadge key={p.id} id={p.id} name={p.name} onClick={props.onClick} />
            ))}
            <loader.LoadTrigger />
        </Stack>
    );
};

export const ViewAllGroupsContainer = (props: { id: string; mode: "parents" | "children" }) => {
    const [popoverOpened, { toggle: togglePopover, close: closePopover }] = useDisclosure(false);

    return (
        <Popover opened={popoverOpened} onChange={togglePopover} trapFocus withArrow shadow="xl">
            <Popover.Target>
                <Badge color="gray" variant="outline" component="button" onClick={togglePopover} style={{ cursor: "pointer" }}>
                    <Group gap="xs">
                        View All <IconChevronDown size={16} />
                    </Group>
                </Badge>
            </Popover.Target>
            <Popover.Dropdown p={0}>
                {match(props.mode)
                    .with("parents", () => <ViewMoreParents childId={props.id} onClick={closePopover} />)
                    .with("children", () => <ViewMoreChildren parentId={props.id} onClick={closePopover} />)
                    .exhaustive()}
            </Popover.Dropdown>
        </Popover>
    );
};
